import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import config from "./config.slice";
import auth from "@/lib/store/auth.slice.ts";
import login from "@/app/auth/login/login.slice";
import forgotPassword from "@/app/auth/forgot-password/forgot-password.slice";
import resetPassword from "@/app/auth/reset-password/reset-password.slice";
import organization from "@/lib/store/organization.slice";
import users from "@/lib/store/users.slice.ts";
import membership from "@/lib/store/membership.slice";
import realtime from "@/lib/store/realtime.slice.ts";
import properties from "@/app/pmc/properties/properties.slice";
import propertyDetails from "@/app/pmc/properties-detail/properties-detail.slice.ts";
import propertyUnits from "@/app/pmc/properties-detail/properties-detail.components/property-units/property-units.slice.ts";
import propertyUsers from "@/app/pmc/properties-detail/properties-detail.components/property-users/property-users.slice.ts";
import vendors from "@/app/pmc/vendors/vendors.slice.ts";
import vendorDetail from "@/app/pmc/vendors/vendors.detail/vendor-detail.slice.ts";
import messages from "@/app/user/messages/messages.slice.ts";
import chatRooms from "@/app/user/messages/chat-rooms.slice.ts";
import signupWizard from "@/app/auth/signup/signup.components/wizard/wizard.slice.ts";
import signup from "@/app/auth/signup/signup.slice.ts";
import services from "@/app/vendors/services/services.slice";
import customPricing from "@/app/vendors/custom-pricing/custom-pricing.slice";
import photoWorkflow from "@/app/vendors/photo-workflow/photo-workflow.slice";
import taskWorkflow from "@/app/vendors/task-workflow/task-workflow.slice";
import surveys from "@/app/vendors/surveys/survey.slice";
import individuals from "@/app/vendors/individuals/individuals.slice";
import individualDetail from "@/app/vendors/individuals/individual-detail.slice";
import subs from "@/app/vendors/subs/subs.slice";
import subsDetail from "@/app/vendors/subs-detail/subs-detail.slice";
import appointments from "@/app/pmc/appointments/appointments.slice";
import vendorAppointments from "@/app/vendors/appointments/appointments.slice";
import { individualAppointmentsReducer, subsAppointmentsReducer } from "@/app/subs/appointments/appointments.slice";
import clients from "@/app/vendors/clients/clients.slice.ts";
import clientsDetail from "@/app/vendors/clients-detail/clients-detail.slice.ts";
import invoices from "@/app/vendors/invoices/invoices.slice";
import dailyWork from "@/app/vendors/daily-work/daily-work.slice";
import paymentDues from "@/app/vendors/payment-dues/payment-dues.slice";
import subsPaymentDues from "@/app/subs/payment-dues/payment-dues.slice";
// Individual slices
import individualAppointmentsNew from "@/app/individual/appointments/appointments.slice";
import dashboard from "@/app/individual/dashboard/dashboard.slice";
import subsDailyWork from "@/app/subs/daily-work/daily-work.slice";
import individualDailyWork from "@/app/individual/daily-work/daily-work.slice";
// Vendor dashboard slice
import vendorDashboard from "@/app/vendors/dashboard/dashboard.slice";
// Subs dashboard slice
import subsDashboard from "@/app/subs/home/dashboard.slice";
import home from "@/app/pmc/home/home.slice";
import account from "@/app/user/account/account.slice.ts";
import notification from "@/app/user/notification/notification.slice";

import organizationSettings from "@/app/organization/settings/organization/organization.slice.ts";
import teamMembers from "@/app/organization/settings/teammembers/teammembers.slice";
import teamMemberDetail from "@/app/organization/settings/teammembers-detail/team-member-detail.slice.ts";

export const store = configureStore({
  reducer: {
    // Global
    config,
    //Auth Reducers
    auth,
    login,
    forgotPassword,
    resetPassword,
    organization,
    membership,
    signupWizard,
    users,
    realtime,
    // Org Reducers
    organizationSettings,
    teamMembers,
    teamMemberDetail,
    //CRUD Reducers PMC
    appointments,
    properties,
    propertyDetails,
    propertyUnits,
    propertyUsers,
    vendors,
    vendorDetail,
    home,
    // CRUD Reducers Vendors
    services,
    customPricing,
    photoWorkflow,
    taskWorkflow,
    surveys,
    individuals,
    individualDetail,
    subs,
    subsDetail,
    vendorAppointments,
    clients,
    clientsDetail,
    invoices,
    dailyWork,
    paymentDues,
    // CRUD Reducers Subs/Individual
    individualAppointments: individualAppointmentsReducer,
    subsAppointments: subsAppointmentsReducer,
    subsDailyWork,
    subsPaymentDues,
    // Individual slices
    individualAppointmentsNew,
    individualDailyWork,
    dashboard,
    // Vendor dashboard
    vendorDashboard,
    // Subs dashboard
    subsDashboard,
    // Messaging System
    messages,
    chatRooms,
    // User
    account,
    notification,
    signup,
  },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
